import { ArrowRight, Sparkles, CheckCircle2, Award, TrendingUp } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

export function Hero() {
  return (
    <div className="pt-24 pb-20 md:pt-32 md:pb-32 relative overflow-hidden">
      {/* Premium gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white" />
      
      {/* Sophisticated decorative elements */}
      <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-600/10 via-purple-500/5 to-transparent rounded-full blur-3xl" />
      <div className="absolute -bottom-20 left-0 w-[700px] h-[700px] bg-gradient-to-tr from-cyan-500/8 via-blue-500/5 to-transparent rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-600/5 to-purple-600/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fadeInUp">
            {/* Premium badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-600/10 via-purple-500/10 to-cyan-500/10 border border-blue-200/50 mb-8 shadow-sm hover:shadow-md transition-shadow">
              <Award size={18} className="text-blue-600" />
              <span className="text-sm text-blue-700 font-semibold">
                Trusted by Fortune 500 & Innovative Startups
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight">
              <span className="gradient-text-premium">
                Accelerate Growth.
              </span>
              <br />
              <span className="text-gray-900">
                Dominate Markets.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 mb-6 leading-relaxed font-medium">
              Elite technology consulting that delivers <span className="text-blue-600 font-semibold">measurable results</span> in weeks, not months.
            </p>

            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              We partner with ambitious leaders to build cutting-edge solutions, eliminate technical debt, and unlock exponential growth. Your competitors are already investing in innovation—don't get left behind.
            </p>

            {/* Key benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              {[
                'ROI-focused implementations',
                'Enterprise-grade security',
                'Scalable architecture',
                '24/7 expert support'
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={16} className="text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{backgroundColor:"#5743ff"}}
                className="group px-8 py-5 text-white rounded-xl transition-all hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-1 flex items-center justify-center gap-3 text-lg font-semibold relative overflow-hidden"
              >
                <span className="relative z-10">Get Your Free Strategy Session</span>
                <ArrowRight size={22} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <button
                onClick={() => {
                  const element = document.getElementById('case-studies');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-5 border-2 border-gray-800 text-gray-800 rounded-xl hover:bg-gray-900 hover:text-white transition-all text-lg font-semibold hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-2"
              >
                <TrendingUp size={20} />
                View Success Stories
              </button>
            </div>

            {/* Enhanced Stats with more impact */}
            <div className="grid grid-cols-3 gap-8 pt-10 border-t-2 border-gray-200">
              <div className="text-center md:text-left">
                <div className="text-4xl md:text-5xl font-extrabold gradient-text-primary mb-2">
                  500+
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  Projects Launched
                </div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-4xl md:text-5xl font-extrabold gradient-text-primary mb-2">
                  $2.5B
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  Client Value Created
                </div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-4xl md:text-5xl font-extrabold gradient-text-primary mb-2">
                  99%
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  Client Retention
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Enhanced Image */}
          <div className="hidden lg:block animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="relative">
              {/* Floating card elements */}
              <div className="absolute -top-8 -left-8 z-20 animate-float">
                <div className="bg-white rounded-2xl shadow-premium p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm font-semibold text-gray-700">Live Project Status</span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">247</div>
                  <div className="text-xs text-gray-600">Active implementations</div>
                </div>
              </div>

              <div className="absolute -bottom-6 -right-6 z-20 animate-float" style={{ animationDelay: '2s' }}>
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-premium-lg p-6 text-white">
                  <div className="text-sm font-semibold mb-1">Average ROI</div>
                  <div className="text-4xl font-bold">385%</div>
                  <div className="text-xs opacity-90">Within 12 months</div>
                </div>
              </div>

              <div className="relative rounded-3xl overflow-hidden shadow-premium-lg border border-gray-200">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-500/5 to-transparent z-10" />
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1646579886741-12b59840c63f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWNobm9sb2d5JTIwY29uc3VsdGluZyUyMHRlYW18ZW58MXx8fHwxNzY3Nzc4MjI2fDA&ixlib=rb-4.1.0&q=80&w=1080"
                  alt="Elite tech consulting team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Trust indicators - Client logos placeholder */}
        <div className="mt-20 pt-12 border-t border-gray-200 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <p className="text-center text-sm text-gray-600 font-semibold uppercase tracking-wider mb-8">
            Trusted by Industry Leaders
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center opacity-60">
            {['TechCorp', 'InnovateLab', 'CloudScale', 'DataVision', 'FutureAI'].map((company, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl font-bold text-gray-400">{company}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}